// src/pages/VaultPage.jsx  (UPDATED)
import React from "react";
import {
    Box, Stack, Typography, Backdrop, CircularProgress, Button, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert, IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import HeaderBar from "../components/HeaderBar";
import themeColors from "../theme";
import { listDocs, uploadDocs, deleteDoc, patchDoc, downloadDoc, sendDocumentSecurely } from "../api/vaultApi";
import VaultFlatTable from "../components/vault/VaultFlatTable";
import VaultOnboarding from "../components/vault/VaultOnboarding";
import QuickUploadDialog from "../components/vault/QuickUploadDialog";
import EditDocDialog from "../components/vault/EditDocDialog";
import SendDocumentDialog from "../components/vault/SendDocumentDialog";

const ONBOARDED_KEY = "vault.onboarded.v1";

const CATEGORIES = [
    { key: "legal", label: "Legal & Identity" },
    { key: "education", label: "Education & Board Certification" },
    { key: "licenses", label: "Medical Licenses" },
    { key: "resuscitation", label: "Resuscitation" },
    { key: "logs", label: "Procedure/Case Logs" },
    { key: "employment", label: "Employment" },
    { key: "health", label: "Health & Immunization" },
    { key: "other", label: "Other/Miscellaneous" },
];

export default function VaultPage() {
    const { lightGray, teal } = themeColors;

    const [docs, setDocs] = React.useState([]);
    const [busy, setBusy] = React.useState(false);
    const [busyMsg, setBusyMsg] = React.useState("");
    const [showOnboarding, setShowOnboarding] = React.useState(
        localStorage.getItem(ONBOARDED_KEY) !== "true"
    );
    const [showQuickUpload, setShowQuickUpload] = React.useState(false);
    const [editDoc, setEditDoc] = React.useState(null);
    const [sendDoc, setSendDoc] = React.useState(null);
    const [sendResult, setSendResult] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    const [filterAnchor, setFilterAnchor] = React.useState(null);
    const [selectedCategories, setSelectedCategories] = React.useState([]);

    const runBusy = async (msg, fn) => {
        setBusyMsg(msg); setBusy(true);
        try { await fn(); } finally { setBusy(false); setBusyMsg(""); }
    };

    const toUi = (d) => ({
        id: d.id || d._id,
        name: d.displayName || d.originalName,
        size: d.size || 0,
        uploadedAt: d.uploadedAt ? new Date(d.uploadedAt) : null,
        expiresAt: d.expiresAt ? new Date(d.expiresAt) : null,
        category: d.slotKey || "other",
    });

    const refresh = React.useCallback(async () => {
        const raw = await listDocs();
        setDocs(raw.map(toUi));
    }, []);

    React.useEffect(() => { runBusy("Loading…", refresh); }, [refresh]);

    const handleOnboardingDone = async () => {
        localStorage.setItem(ONBOARDED_KEY, "true");
        setShowOnboarding(false);
        await runBusy("Refreshing…", refresh);
    };

    const handleQuickUpload = async ({ file, name, category, expiresAt }) => {
        await runBusy("Uploading…", async () => {
            await uploadDocs({ files: [file], slotKey: category, displayName: name, expiresAt: expiresAt || null, makeCurrent: true });
            await refresh();
        });
    };

    const onDelete = async (docId) => {
        await runBusy("Deleting…", async () => {
            await deleteDoc(docId);
            await refresh();
        });
    };

    const onEdit = (doc) => setEditDoc(doc);

    const onSaveEdit = async ({ id, name, expiresAt, category }) => {
        await runBusy("Saving…", async () => {
            await patchDoc(id, { displayName: name, expiresAt, slotKey: category });
            setEditDoc(null);
            await refresh();
        });
    };

    const onDownload = async (docId) => {
        // Open in a new tab
        await runBusy("Preparing file…", async () => {
            const { url } = await downloadDoc(docId, { delivery: "browser" });
            if (url) window.open(url, "_blank", "noopener,noreferrer");
        });
    };

    const onSend = (doc) => setSendDoc(doc);

    const onSendSubmit = async ({ docId, recipientEmail, message, encrypted, expiresInDays, requireVerification }) => {
        await runBusy("Sending document…", async () => {
            const result = await sendDocumentSecurely(docId, { recipientEmail, message, encrypted, expiresInDays, requireVerification });
            setSendDoc(null);
            setSendResult(result);
            setCopied(false);
        });
    };

    const handleCopyLink = async () => {
        if (sendResult?.accessUrl) {
            try {
                await navigator.clipboard.writeText(sendResult.accessUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error("Failed to copy:", err);
            }
        }
    };

    const handleToggleCategory = (categoryKey) => {
        setSelectedCategories((prev) =>
            prev.includes(categoryKey)
                ? prev.filter((c) => c !== categoryKey)
                : [...prev, categoryKey]
        );
    };

    const filteredDocs = selectedCategories.length === 0
        ? docs
        : docs.filter((d) => selectedCategories.includes(d.category));

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            <HeaderBar />

            <Box sx={{ px: 4, pt: 4, pb: 6, maxWidth: 1400, mx: "auto" }}>
                <Stack spacing={1} alignItems="center" sx={{ mb: 4 }}>
                    <Typography variant="h3" fontWeight="bold" color="#2C3E50">The Vault</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center" }}>
                        Securely store licenses, certifications, and important documents for credentialing and job applications.
                    </Typography>
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => setShowQuickUpload(true)}
                        sx={{
                            backgroundColor: teal,
                            color: "#fff",
                            textTransform: "none",
                            px: 3,
                            py: 1.5,
                            fontWeight: 600,
                            borderRadius: 2,
                            "&:hover": { backgroundColor: themeColors.darkTeal }
                        }}
                        startIcon={<AddIcon />}
                    >
                        Upload Document
                    </Button>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        overflow: "hidden"
                    }}
                >
                    <Box sx={{ p: 2.5, borderBottom: "1px solid #e0e0e0" }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FolderOpenOutlinedIcon fontSize="small" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Uploaded Documents ({filteredDocs.length}{selectedCategories.length > 0 ? ` of ${docs.length}` : ""})
                                </Typography>
                            </Stack>
                            <IconButton
                                onClick={(e) => setFilterAnchor(e.currentTarget)}
                                sx={{
                                    color: selectedCategories.length > 0 ? teal : "text.secondary",
                                    backgroundColor: selectedCategories.length > 0 ? `${teal}15` : "transparent",
                                    "&:hover": { backgroundColor: selectedCategories.length > 0 ? `${teal}25` : "#F5F5F5" }
                                }}
                                size="small"
                            >
                                <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Menu
                                anchorEl={filterAnchor}
                                open={Boolean(filterAnchor)}
                                onClose={() => setFilterAnchor(null)}
                                PaperProps={{ sx: { minWidth: 220, borderRadius: 2 } }}
                            >
                                {CATEGORIES.map((cat) => (
                                    <MenuItem
                                        key={cat.key}
                                        onClick={() => handleToggleCategory(cat.key)}
                                        dense
                                    >
                                        <Checkbox
                                            checked={selectedCategories.includes(cat.key)}
                                            size="small"
                                            sx={{ color: teal, "&.Mui-checked": { color: teal } }}
                                        />
                                        <ListItemText primary={cat.label} />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Stack>
                    </Box>
                    <VaultFlatTable
                        docs={filteredDocs}
                        onView={onDownload}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onSend={onSend}
                    />
                    <Box sx={{ p: 2, textAlign: "center", borderTop: "1px solid #e0e0e0" }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredDocs.length} document{filteredDocs.length !== 1 ? "s" : ""}{selectedCategories.length > 0 ? ` (filtered from ${docs.length})` : ""}.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <VaultOnboarding open={showOnboarding} onClose={handleOnboardingDone} onUploaded={refresh} />

            <QuickUploadDialog
                open={showQuickUpload}
                onClose={() => setShowQuickUpload(false)}
                onSubmit={async (payload) => {
                    await handleQuickUpload(payload);
                    setShowQuickUpload(false);
                }}
            />

            <EditDocDialog
                open={!!editDoc}
                doc={editDoc}
                onClose={() => setEditDoc(null)}
                onSubmit={onSaveEdit}
            />

            <SendDocumentDialog
                open={!!sendDoc}
                doc={sendDoc}
                onClose={() => setSendDoc(null)}
                onSubmit={onSendSubmit}
            />

            {/* Success Dialog after sending */}
            <Dialog
                open={!!sendResult}
                onClose={() => setSendResult(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#4CAF50" }} />
                    <Typography variant="h6" fontWeight={600}>Document Shared</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        {sendResult?.emailSent ? (
                            <Alert severity="success" sx={{ borderRadius: 1 }}>
                                An email has been sent to <strong>{sendResult?.recipientEmail}</strong> with the secure link.
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ borderRadius: 1 }}>
                                Email notification could not be sent. Please share the link manually.
                            </Alert>
                        )}

                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Secure share link:
                            </Typography>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                backgroundColor: "#f5f5f5",
                                p: 1.5,
                                borderRadius: 1,
                                border: "1px solid #e0e0e0"
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: 1,
                                        wordBreak: "break-all",
                                        fontFamily: "monospace",
                                        fontSize: "0.75rem"
                                    }}
                                >
                                    {sendResult?.accessUrl}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={handleCopyLink}
                                    sx={{ color: copied ? "#4CAF50" : "text.secondary" }}
                                >
                                    {copied ? <CheckCircleOutlineIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setSendResult(null)}
                        sx={{
                            backgroundColor: teal,
                            "&:hover": { backgroundColor: themeColors.darkTeal },
                            textTransform: "none",
                            fontWeight: 600
                        }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <Backdrop open={busy} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}>
                <Stack alignItems="center" spacing={1}>
                    <CircularProgress color="inherit" />
                    <Typography variant="body2">{busyMsg || "Working…"}</Typography>
                </Stack>
            </Backdrop>
        </Box>
    );
}